import React, { useState } from 'react';
import { Alert, Button, Col, Descriptions, Form, InputNumber, Modal, Row, Typography, message } from 'antd';
import { adjustSettlementAmount, AdjustSettlementAmountResponse } from '../../../services/supportSuite';

interface FormValues {
  idSolicitud: number;
  monto: number;
}

const AdjustSettlementAmount: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AdjustSettlementAmountResponse['data']>();

  const submitAdjustment = async (values: FormValues) => {
    setLoading(true);
    const hide = message.loading('Ajustando monto de liquidacion...', 0);

    try {
      const response = await adjustSettlementAmount({
        idSolicitud: Number(values.idSolicitud),
        monto: Number(values.monto),
      });

      message.success(response.message || 'Ajuste realizado con exito');
      setResult(response.data);
      form.resetFields();
    } catch (error) {
      const apiError = error as any;
      message.error(apiError?.response?.data?.message || 'No fue posible ajustar el monto de liquidacion');
    } finally {
      hide();
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      const values = (await form.validateFields()) as FormValues;

      Modal.confirm({
        title: 'Confirmar ajuste de monto',
        okText: 'Ajustar monto',
        cancelText: 'Cancelar',
        content: (
          <div>
            <Typography.Paragraph>
              Se actualizara el monto en MMV de todas las liquidaciones asociadas a la solicitud indicada.
            </Typography.Paragraph>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              Solicitud: <strong>{values.idSolicitud}</strong>
              <br />
              Nuevo monto global en Bs: <strong>{Number(values.monto).toFixed(2)}</strong>
            </Typography.Paragraph>
          </div>
        ),
        onOk: () => submitAdjustment(values),
      });
    } catch (_error) {
      return undefined;
    }

    return undefined;
  };

  return (
    <div>
      <Typography.Paragraph>
        Ajusta el monto total en bolivares de una solicitud.
      </Typography.Paragraph>

      <Alert
        showIcon
        type='warning'
        style={{ marginBottom: 16 }}
        message='Impacto del ajuste'
        description='La operacion afecta todas las liquidaciones de la solicitud, recalcula el monto en MMV con el valor vigente y registra auditoria por cada liquidacion.'
      />

      <Form layout='vertical' form={form} scrollToFirstError>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name='idSolicitud'
              label='Identificador de Solicitud'
              rules={[
                { required: true, message: 'Debe indicar la solicitud' },
                {
                  validator: (_rule, value) => {
                    if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
                      return Promise.resolve();
                    }

                    return Promise.reject('Debe ingresar un identificador valido');
                  },
                },
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={1} precision={0} placeholder='Ej. 12345' />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name='monto'
              label='Nuevo Monto en Bs'
              rules={[
                { required: true, message: 'Debe indicar el monto' },
                {
                  validator: (_rule, value) => {
                    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
                      return Promise.resolve();
                    }

                    return Promise.reject('Debe ingresar un monto mayor a cero');
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0.01}
                precision={2}
                placeholder='Ej. 8500.25'
                parser={(value) => (value ? value.replace(/[^\d.,-]/g, '').replace(',', '.') : '')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Button type='primary' loading={loading} onClick={handleConfirm}>
          Ajustar Monto
        </Button>
      </Form>

      {result && (
        <Descriptions title='Ultimo ajuste realizado' bordered size='small' style={{ marginTop: 24 }} column={1}>
          <Descriptions.Item label='Solicitud'>{result.idSolicitud}</Descriptions.Item>
          <Descriptions.Item label='Liquidaciones afectadas'>{result.cantidadLiquidaciones}</Descriptions.Item>
          <Descriptions.Item label='Monto MMV por liquidacion'>
            {result.montoPetroPorLiquidacion}
          </Descriptions.Item>
        </Descriptions>
      )}
    </div>
  );
};

export default AdjustSettlementAmount;
